import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { MapPin, Mail, Clock, Phone } from 'lucide-react';

const BOOKING_URL = 'https://form.fillout.com/t/6Qfrr3aggsus';
const GOOGLE_MAPS_URL = 'https://maps.app.goo.gl/sy4UXwacmxKZjqks7';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.15, ease: 'easeOut' as const },
  }),
};

const Contact = ({ onBooking }: { onBooking?: () => void }) => {
  const { t } = useTranslation();

  const phones = [
    { label: 'Arredo', number: '+39 348 820 4372' },
    { label: 'Arte', number: '+39 346 376 0606' },
    { label: 'Luce', number: '+39 336 66 8833' },
    { label: 'Marmi', number: '+39 333 323 0512' },
  ];

  return (
    <section id="contatti" className="py-24 px-6 bg-card">
      <div className="max-w-7xl mx-auto">
        {/* Top row: contacts + booking side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left: info */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.span variants={fadeUp} custom={0} className="text-xs tracking-[0.3em] uppercase text-primary block mb-4 font-medium">
              {t('contact.label')}
            </motion.span>
            <motion.h2 variants={fadeUp} custom={1} className="text-4xl md:text-6xl text-foreground mb-10 leading-tight font-medium">
              {t('contact.title')}
            </motion.h2>

            <div className="space-y-5">
              {/* Address */}
              <motion.div variants={fadeUp} custom={2} className="flex items-center gap-4">
                <MapPin className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={1.5} />
                <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="text-muted-foreground font-light hover:text-foreground transition-colors">
                  {t('contact.address')}
                </a>
              </motion.div>

              {/* Email */}
              <motion.div variants={fadeUp} custom={3} className="flex items-center gap-4">
                <Mail className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={1.5} />
                <a href="mailto:info@atelierelements.it" className="text-muted-foreground font-light hover:text-foreground transition-colors">
                  {t('contact.email')}
                </a>
              </motion.div>

              {/* Phones */}
              <motion.div variants={fadeUp} custom={4} className="flex items-start gap-4">
                <Phone className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div className="space-y-1">
                  {phones.map((p) => (
                    <p key={p.label} className="text-muted-foreground font-light">
                      <span className="text-foreground font-medium">{p.label}:</span>{' '}
                      <a href={`tel:${p.number.replace(/\s/g, '')}`} className="hover:text-foreground transition-colors">
                        {p.number}
                      </a>
                    </p>
                  ))}
                </div>
              </motion.div>

              {/* Hours */}
              <motion.div variants={fadeUp} custom={5} className="flex items-start gap-4">
                <Clock className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div className="text-muted-foreground font-light">
                  <p><span className="text-foreground font-medium">{t('contact.weekdays')}</span> 10:00 – 19:00</p>
                  <p>{t('contact.sunday')}</p>
                </div>
              </motion.div>

              <motion.p variants={fadeUp} custom={6} className="text-xs text-muted-foreground/70 italic pl-9">
                {t('contact.hoursNote')}
              </motion.p>
            </div>
          </motion.div>

          {/* Right: book visit */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="flex items-center"
          >
            <motion.div
              variants={fadeUp}
              custom={0}
              className="w-full p-14 border border-border text-center rounded-2xl"
            >
              <div className="w-16 h-px bg-primary mx-auto mb-10" />
              <h3 className="text-3xl md:text-4xl text-foreground mb-6 font-medium">
                {t('contact.bookTitle')}
              </h3>
              <p className="text-muted-foreground font-light leading-relaxed mb-12 max-w-md mx-auto">
                {t('contact.bookDesc')}
              </p>
              <button
                onClick={onBooking}
                className="inline-block px-12 py-4 text-sm tracking-[0.1em] uppercase bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 rounded-full font-medium whitespace-nowrap"
              >
                {t('contact.bookCta')}
              </button>
              <div className="w-16 h-px bg-primary mx-auto mt-10" />
            </motion.div>
          </motion.div>
        </div>

        {/* Full-width map below */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-16"
        >
          <motion.div
            variants={fadeUp}
            custom={0}
            className="aspect-square md:aspect-[21/9] bg-secondary overflow-hidden rounded-lg"
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2798.0!2d9.1835!3d45.4725!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4786c6a5b0c8c5e1%3A0x1!2sVia+S.+Simpliciano%2C+6%2C+20121+Milano+MI!5e0!3m2!1sit!2sit!4v1"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Atelier Elements Location"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
